import { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Search, Upload, FileText, Music, Video, Image, ArrowUpDown,
  ChevronDown, Loader2, X, Library, SlidersHorizontal, CreditCard, Coins, LogOut
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import UploadDialog from '@/components/UploadDialog';
import DocumentCard from '@/components/DocumentCard';
import DocumentDetailModal from '@/components/DocumentDetailModal';
import LibraryCardModal from '@/components/LibraryCardModal';
import RedeemTokenModal from '@/components/RedeemTokenModal';
import { useLibraryCard } from '@/components/LibraryCardContext';

const SORT_OPTIONS = [
  { value: 'newest', label: 'Newest first' },
  { value: 'oldest', label: 'Oldest first' },
  { value: 'az', label: 'A — Z' },
  { value: 'za', label: 'Z — A' },
  { value: 'largest', label: 'Largest' },
];

const TYPE_FILTERS = [
  { value: null, label: 'All', icon: Library },
  { value: 'document', label: 'Docs', icon: FileText },
  { value: 'image', label: 'Images', icon: Image },
  { value: 'audio', label: 'Audio', icon: Music },
  { value: 'video', label: 'Video', icon: Video },
];

const PAGE_SIZE = 60;

const DocumentLibrary = ({ apiUrl, backendUrl, onAnalyze }) => {
  const libraryCard = useLibraryCard();
  const [documents, setDocuments] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedType, setSelectedType] = useState(null);
  const [sortBy, setSortBy] = useState('newest');
  const [showSortMenu, setShowSortMenu] = useState(false);
  const [showCategories, setShowCategories] = useState(false);
  const [categories, setCategories] = useState([]);
  const [categoryCounts, setCategoryCounts] = useState({});
  const [stats, setStats] = useState(null);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [showCardModal, setShowCardModal] = useState(false);
  const [showRedeemModal, setShowRedeemModal] = useState(false);

  const sortDocuments = useCallback((docs) => {
    const sorted = [...docs];
    switch (sortBy) {
      case 'newest': return sorted.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      case 'oldest': return sorted.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
      case 'az': return sorted.sort((a, b) => a.title.localeCompare(b.title));
      case 'za': return sorted.sort((a, b) => b.title.localeCompare(a.title));
      case 'largest': return sorted.sort((a, b) => (b.file_size || 0) - (a.file_size || 0));
      default: return sorted;
    }
  }, [sortBy]);

  const fetchDocuments = async (pageNum = 1, append = false) => {
    try {
      if (pageNum === 1) setLoading(true);
      else setLoadingMore(true);

      const params = { page: pageNum, page_size: PAGE_SIZE };
      if (selectedCategory) params.category = selectedCategory;
      if (selectedType) params.type = selectedType;
      if (searchTerm) params.search = searchTerm;
      if (libraryCard?.isAdmin) params.include_restricted = true;

      const response = await axios.get(`${apiUrl}/documents`, { params });
      const newDocs = response.data.documents;
      setTotal(response.data.total);

      if (append) {
        setDocuments(prev => sortDocuments([...prev, ...newDocs]));
      } else {
        setDocuments(sortDocuments(newDocs));
      }
      setPage(pageNum);
    } catch (error) {
      console.error('Error fetching documents:', error);
      toast.error('Failed to load documents');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${apiUrl}/categories`);
      setCategories(response.data.categories);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchStats = async () => {
    try {
      const params = {};
      if (libraryCard?.isAdmin) params.include_restricted = true;
      const response = await axios.get(`${apiUrl}/stats`, { params });
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  useEffect(() => {
    const fetchCounts = async () => {
      try {
        const res = await axios.get(`${apiUrl}/documents`, { params: { page: 1, page_size: 1 } });
        const counts = { 'All': res.data.total };
        for (const cat of categories) {
          const catRes = await axios.get(`${apiUrl}/documents`, { params: { page: 1, page_size: 1, category: cat } });
          counts[cat] = catRes.data.total;
        }
        setCategoryCounts(counts);
      } catch (e) { console.error('Category count fetch error:', e); }
    };
    if (categories.length > 0) fetchCounts();
  }, [categories, apiUrl]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchDocuments(1); }, [searchTerm, selectedCategory, selectedType, libraryCard?.isAdmin]);
  useEffect(() => { setDocuments(prev => sortDocuments(prev)); }, [sortBy, sortDocuments]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchCategories(); fetchStats(); }, [libraryCard?.isAdmin]);

  // Deep-link: /library?open=<docId> opens that document (from Outside Voice "Read from KEYS" links)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const openId = params.get('open');
    if (!openId) return;
    axios.get(`${apiUrl}/documents/${openId}`)
      .then((res) => setSelectedDocument(res.data))
      .catch(() => toast.error('That document could not be opened'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleUploadSuccess = () => {
    fetchDocuments(1);
    fetchCategories();
    fetchStats();
    setUploadDialogOpen(false);
    toast.success('Document uploaded successfully!');
  };

  const handleDeleteDocument = async (docId) => {
    try {
      await axios.delete(`${apiUrl}/documents/${docId}`);
      toast.success('Document deleted');
      fetchDocuments(1);
      fetchStats();
      setSelectedDocument(null);
    } catch (error) {
      toast.error('Failed to delete document');
    }
  };

  const loadMore = () => fetchDocuments(page + 1, true);
  const hasMore = documents.length < total;

  const clearFilters = () => {
    setSelectedCategory(null);
    setSelectedType(null);
    setSearchTerm('');
  };

  const hasActiveFilters = selectedCategory || selectedType || searchTerm;

  // Keyboard shortcuts 1-6 for quick-nav
  useEffect(() => {
    const handler = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      const map = {
        '1': () => { setSelectedCategory('podcast'); setSelectedType(null); },
        '2': () => { setSelectedType('audio'); setSelectedCategory(null); },
        '3': () => { setSelectedType('video'); setSelectedCategory(null); },
        '4': () => { setSelectedCategory('blog'); setSelectedType(null); },
        '5': () => { setSelectedCategory('HumanCodex'); setSelectedType(null); },
        '6': () => { setSelectedCategory('Federation Core'); setSelectedType(null); },
        '0': () => clearFilters(),
      };
      if (map[e.key]) { e.preventDefault(); map[e.key](); }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  return (
    <div className="min-h-screen bg-slate-950" data-testid="document-library">
      {/* ─── Sticky Header ─── */}
      <div className="sticky top-0 z-40 bg-slate-950/95 backdrop-blur-xl border-b border-slate-800/40">
        <div className="max-w-[1500px] mx-auto px-6 py-4">
          {/* Top row: Title + Upload */}
          <div className="flex items-end justify-between mb-4">
            <div>
              <h1 className="text-2xl font-bold text-white tracking-tight" data-testid="library-title">
                The Library
              </h1>
              <p className="text-sm text-slate-500 mt-0.5">
                {stats ? `${stats.total_documents} assets` : 'Loading...'} across the NextXus Federation
              </p>
            </div>
            <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
              <DialogTrigger asChild>
                <Button className="gap-2 bg-cyan-600 hover:bg-cyan-500 text-sm font-medium px-5" data-testid="upload-button">
                  <Upload className="w-4 h-4" /> Upload
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-md bg-slate-950 border-slate-800">
                <DialogHeader><DialogTitle>Upload Document</DialogTitle></DialogHeader>
                <UploadDialog apiUrl={apiUrl} onSuccess={handleUploadSuccess} />
              </DialogContent>
            </Dialog>
          </div>

          {/* Library Card / Wallet Bar */}
          <div className="flex items-center gap-3 mb-4 bg-slate-900/40 rounded-xl border border-slate-800/40 px-4 py-2.5">
            {libraryCard?.hasCard ? (
              <>
                <CreditCard className="w-4 h-4 text-cyan-400" />
                <span className="text-xs text-slate-400">
                  <span className="text-white font-medium">{libraryCard.card?.display_name || libraryCard.card?.email}</span>
                </span>
                <div className="h-4 w-px bg-slate-700 mx-1" />
                <Coins className="w-4 h-4 text-amber-400" />
                <span className="text-xs font-bold text-amber-300" data-testid="token-balance">
                  {libraryCard.isAdmin ? '∞' : libraryCard.tokenBalance} {!libraryCard.isAdmin && libraryCard.tokenBalance === 1 ? 'token' : 'tokens'}
                </span>
                {libraryCard.isAdmin && (
                  <Badge className="text-[9px] bg-amber-600/20 text-amber-400 border border-amber-500/30 ml-1">ADMIN PASS</Badge>
                )}
                {!libraryCard.isAdmin && (
                  <Button variant="outline" size="sm" className="text-[11px] h-7 px-2.5 gap-1 ml-2" onClick={() => setShowRedeemModal(true)} data-testid="buy-tokens-header-btn">
                    <Coins className="w-3 h-3" /> Buy Tokens
                  </Button>
                )}
                <div className="flex-1" />
                <button onClick={(e) => { e.preventDefault(); e.stopPropagation(); libraryCard.logout(); }} className="text-xs text-slate-500 hover:text-white flex items-center gap-1 transition-colors" data-testid="logout-card-btn">
                  <LogOut className="w-3 h-3" /> Sign out
                </button>
              </>
            ) : (
              <>
                <CreditCard className="w-4 h-4 text-slate-500" />
                <span className="text-xs text-slate-500">No library card</span>
                <div className="flex-1" />
                <Button variant="outline" size="sm" className="text-[11px] h-7 px-3 gap-1.5 border-cyan-700/50 text-cyan-400" onClick={() => setShowCardModal(true)} data-testid="get-card-header-btn">
                  <CreditCard className="w-3 h-3" /> Get Library Card
                </Button>
              </>
            )}
          </div>

          {/* Search + Type Filters + Sort Row */}
          <div className="flex items-center gap-3">
            {/* Search */}
            <div className="relative flex-1 max-w-lg">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 w-4 h-4" />
              <Input
                type="text"
                placeholder={`Search ${stats?.total_documents || ''} documents...`}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 h-10 text-sm bg-slate-900/60 border-slate-800/60 focus:border-cyan-700/50 rounded-lg"
                data-testid="search-input"
              />
              {searchTerm && (
                <button onClick={() => setSearchTerm('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white">
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>

            {/* Type filter pills */}
            <div className="flex items-center gap-1 bg-slate-900/40 rounded-lg p-1 border border-slate-800/40">
              {TYPE_FILTERS.map(f => {
                const Icon = f.icon;
                const isActive = selectedType === f.value;
                const count = f.value ? (stats?.by_type?.[f.value] || 0) : (stats?.total_documents || 0);
                return (
                  <button
                    key={f.label}
                    onClick={() => setSelectedType(f.value)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all duration-200 ${
                      isActive
                        ? 'bg-slate-800 text-white shadow-sm'
                        : 'text-slate-500 hover:text-slate-300'
                    }`}
                    data-testid={`filter-${f.value || 'all'}`}
                  >
                    <Icon className="w-3.5 h-3.5" />
                    <span className="hidden sm:inline">{f.label}</span>
                    <span className={`text-[10px] ${isActive ? 'text-cyan-400' : 'text-slate-600'}`}>{count}</span>
                  </button>
                );
              })}
            </div>

            {/* Category toggle */}
            <Button
              variant="outline"
              size="sm"
              className={`h-10 text-xs gap-1.5 ${showCategories || selectedCategory ? 'border-cyan-700/50 text-cyan-400' : ''}`}
              onClick={() => setShowCategories(!showCategories)}
              data-testid="categories-toggle"
            >
              <SlidersHorizontal className="w-3.5 h-3.5" />
              {selectedCategory || 'Categories'}
              {selectedCategory && (
                <button onClick={(e) => { e.stopPropagation(); setSelectedCategory(null); }} className="ml-1">
                  <X className="w-3 h-3" />
                </button>
              )}
            </Button>

            {/* Sort */}
            <div className="relative">
              <Button
                variant="outline"
                size="sm"
                className="h-10 text-xs gap-1.5"
                onClick={() => setShowSortMenu(!showSortMenu)}
                data-testid="sort-button"
              >
                <ArrowUpDown className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">{SORT_OPTIONS.find(o => o.value === sortBy)?.label}</span>
                <ChevronDown className="w-3 h-3" />
              </Button>
              {showSortMenu && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setShowSortMenu(false)} />
                  <div className="absolute right-0 top-full mt-1 bg-slate-900 border border-slate-700 rounded-lg shadow-2xl z-20 py-1 min-w-[160px]">
                    {SORT_OPTIONS.map(o => (
                      <button
                        key={o.value}
                        className={`w-full text-left px-4 py-2 text-xs hover:bg-slate-800 transition-colors ${
                          sortBy === o.value ? 'text-cyan-400 bg-slate-800/50' : 'text-slate-300'
                        }`}
                        onClick={() => { setSortBy(o.value); setShowSortMenu(false); }}
                        data-testid={`sort-${o.value}`}
                      >
                        {o.label}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Category chips row (collapsible) */}
        {showCategories && (
          <div className="border-t border-slate-800/30 bg-slate-950/80">
            <div className="max-w-[1500px] mx-auto px-6 py-3">
              <ScrollArea className="w-full" orientation="horizontal">
                <div className="flex gap-2 pb-1">
                  <button
                    onClick={() => setSelectedCategory(null)}
                    className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200 ${
                      !selectedCategory
                        ? 'bg-cyan-600/20 text-cyan-400 border border-cyan-500/30'
                        : 'bg-slate-900/60 text-slate-400 border border-slate-800/60 hover:text-white hover:border-slate-700'
                    }`}
                    data-testid="category-all"
                  >
                    All {categoryCounts['All'] ? `(${categoryCounts['All']})` : ''}
                  </button>
                  {[...categories].sort().map(cat => (
                    <button
                      key={cat}
                      onClick={() => { setSelectedCategory(cat); setShowCategories(false); }}
                      className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200 ${
                        selectedCategory === cat
                          ? 'bg-cyan-600/20 text-cyan-400 border border-cyan-500/30'
                          : 'bg-slate-900/60 text-slate-400 border border-slate-800/60 hover:text-white hover:border-slate-700'
                      }`}
                      data-testid={`category-${cat}`}
                    >
                      {cat} {categoryCounts[cat] ? `(${categoryCounts[cat]})` : ''}
                    </button>
                  ))}
                </div>
              </ScrollArea>
            </div>
          </div>
        )}
      </div>

      {/* ─── Quick-Nav Category Bar (Keyboard: 1-6) ─── */}
      <div className="bg-slate-900/40 border-b border-slate-800/30">
        <div className="max-w-[1500px] mx-auto px-6 py-2 flex items-center gap-2 overflow-x-auto">
          {[
            { key: '1', label: 'Podcasts', cat: 'podcast', icon: '🎧', color: 'rose' },
            { key: '2', label: 'Audio', type: 'audio', icon: '🎵', color: 'amber' },
            { key: '3', label: 'Video', type: 'video', icon: '🎬', color: 'violet' },
            { key: '4', label: 'Articles', cat: 'blog', icon: '📝', color: 'cyan' },
            { key: '5', label: 'HumanCodex', cat: 'HumanCodex', icon: '📖', color: 'amber' },
            { key: '6', label: 'Federation', cat: 'Federation Core', icon: '🌐', color: 'emerald' },
          ].map(q => (
            <button
              key={q.key}
              onClick={() => { if (q.cat) { setSelectedCategory(q.cat); setSelectedType(null); } else { setSelectedType(q.type); setSelectedCategory(null); } }}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium transition-all flex-shrink-0 ${
                (q.cat && selectedCategory === q.cat) || (q.type && selectedType === q.type)
                  ? `bg-${q.color}-500/20 text-${q.color}-400 border border-${q.color}-500/30`
                  : 'bg-slate-800/40 text-slate-500 border border-slate-800/30 hover:text-white hover:bg-slate-800/60'
              }`}
              data-testid={`quicknav-${q.key}`}
            >
              <kbd className="text-[9px] bg-slate-700/50 px-1 py-0.5 rounded font-mono">{q.key}</kbd>
              <span>{q.icon}</span>
              {q.label}
            </button>
          ))}
          <button
            onClick={clearFilters}
            className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs text-slate-600 hover:text-white transition-all flex-shrink-0"
          >
            <kbd className="text-[9px] bg-slate-700/50 px-1 py-0.5 rounded font-mono">0</kbd>
            All
          </button>
        </div>
      </div>

      {/* ─── Main Content ─── */}
      <div className="max-w-[1500px] mx-auto px-6 py-6">
        {/* Active filters bar */}
        {hasActiveFilters && (
          <div className="flex items-center gap-2 mb-5 animate-in fade-in slide-in-from-top-1 duration-200">
            <span className="text-[11px] text-slate-600 uppercase tracking-wider">Filters:</span>
            {selectedCategory && (
              <Badge variant="outline" className="text-xs cursor-pointer gap-1 border-cyan-800/40 text-cyan-400" onClick={() => setSelectedCategory(null)}>
                {selectedCategory} <X className="w-3 h-3" />
              </Badge>
            )}
            {selectedType && (
              <Badge variant="outline" className="text-xs cursor-pointer gap-1 border-slate-700 text-slate-300" onClick={() => setSelectedType(null)}>
                {selectedType} <X className="w-3 h-3" />
              </Badge>
            )}
            {searchTerm && (
              <Badge variant="outline" className="text-xs cursor-pointer gap-1 border-slate-700 text-slate-300" onClick={() => setSearchTerm('')}>
                "{searchTerm}" <X className="w-3 h-3" />
              </Badge>
            )}
            <button onClick={clearFilters} className="text-[11px] text-slate-500 hover:text-white ml-2 underline underline-offset-2">
              Clear all
            </button>
            <span className="text-[11px] text-slate-600 ml-auto">{total} results</span>
          </div>
        )}

        {/* Document Grid */}
        {loading ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
            {[...Array(15)].map((_, i) => (
              <div key={i} className="animate-pulse rounded-lg overflow-hidden">
                <div className="h-36 bg-slate-900/60 rounded-t-lg" />
                <div className="bg-slate-900/40 p-3 rounded-b-lg">
                  <div className="h-4 bg-slate-800/60 rounded mb-2" />
                  <div className="h-3 bg-slate-800/40 rounded w-2/3" />
                </div>
              </div>
            ))}
          </div>
        ) : documents.length === 0 ? (
          <div className="text-center py-24">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-slate-900/60 border border-slate-800/60 mb-4">
              <FileText className="w-8 h-8 text-slate-600" />
            </div>
            <h3 className="text-base font-medium text-white mb-1">No documents found</h3>
            <p className="text-sm text-slate-500 mb-6">
              {hasActiveFilters ? 'Try adjusting your filters' : 'Upload your first document to get started'}
            </p>
            {hasActiveFilters && (
              <Button variant="outline" size="sm" onClick={clearFilters} className="text-xs">
                Clear filters
              </Button>
            )}
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4" data-testid="document-grid">
              {documents.map((doc, i) => (
                <DocumentCard
                  key={doc.id}
                  document={doc}
                  backendUrl={backendUrl}
                  index={i}
                  onClick={() => setSelectedDocument(doc)}
                  onAnalyze={onAnalyze ? () => onAnalyze(doc.id) : undefined}
                />
              ))}
            </div>

            {hasMore && (
              <div className="flex justify-center mt-8">
                <Button
                  variant="outline"
                  onClick={loadMore}
                  disabled={loadingMore}
                  className="text-sm gap-2 px-8 py-5 rounded-xl border-slate-700 hover:border-slate-600"
                  data-testid="load-more"
                >
                  {loadingMore ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                  Show more
                  <span className="text-slate-500 text-xs">({documents.length} of {total})</span>
                </Button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Document Detail Modal */}
      {selectedDocument && (
        <DocumentDetailModal
          document={selectedDocument}
          backendUrl={backendUrl}
          apiUrl={apiUrl}
          open={!!selectedDocument}
          onClose={() => setSelectedDocument(null)}
          onDelete={handleDeleteDocument}
          onAnalyze={onAnalyze}
          onRequestCard={() => { setSelectedDocument(null); setShowCardModal(true); }}
          onRequestTokens={() => { setSelectedDocument(null); setShowRedeemModal(true); }}
          onDocUpdated={() => fetchDocuments(1)}
        />
      )}

      {/* Library Card Registration Modal */}
      <LibraryCardModal
        open={showCardModal}
        onClose={() => setShowCardModal(false)}
        onSuccess={() => setShowCardModal(false)}
      />

      {/* Token Redemption Modal */}
      <RedeemTokenModal
        open={showRedeemModal}
        onClose={() => setShowRedeemModal(false)}
      />
    </div>
  );
};

export default DocumentLibrary;
